import React, { useCallback, useEffect, useState } from "react";
import { maintenanceApi, ProcPoolSummary } from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import PoolTab from "./PoolTab";
import OrdersTab from "./OrdersTab";
import ManufacturersTab from "./ManufacturersTab";
import BatchesTab from "./BatchesTab";
import SimsTab from "./SimsTab";
import MintBatchForm from "./MintBatchForm";
import ImportCsvForm from "./ImportCsvForm";
import { Plus, Upload } from "lucide-react";
import "../stations/maintenance-stations.scss";
import "./maintenance-procurement.scss";
import { useNotice } from "../shared/useNotice";

type Tab = "pool" | "orders" | "sims" | "manufacturers" | "batches";

/**
 * Device procurement console.
 *
 * Covers the life of a device BEFORE it is a device: mint an identity, order it
 * from a manufacturer, receive it (in however many shipments it actually turns
 * up in), then onboard it with the identity pre-filled and locked.
 */
const ProcurementConsole: React.FC = () => {
  const [tab, setTab] = useState<Tab>("pool");
  const [summary, setSummary] = useState<ProcPoolSummary | null>(null);
  const [showMint, setShowMint] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [message, setMessage] = useNotice("success", "");
  const [error, setError] = useNotice("error", "");
  // bumped whenever something changes, so the active tab refetches
  const [refreshKey, setRefreshKey] = useState(0);

  const canEdit = canEditMaintenance();

  const loadSummary = useCallback(() => {
    maintenanceApi
      .procGetSummary()
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null));
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, refreshKey]);

  const refresh = useCallback((msg?: string) => {
    if (msg) {
      setMessage(msg);
      setError("");
    }
    setRefreshKey((k) => k + 1);
  }, []);

  const tabs: { key: Tab; label: string }[] = [
    { key: "pool", label: "Device pool" },
    { key: "orders", label: "Orders" },
    { key: "sims", label: "SIMs" },
    { key: "manufacturers", label: "Manufacturers" },
    { key: "batches", label: "Batches" },
  ];

  return (
    <MaintenanceLayout title="Device Procurement">
      <div className="proc maint-stations">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 4,
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: "#666", maxWidth: 680 }}>
            Track a device from the moment its ID is minted, through the order to the
            manufacturer and back, to onboarding. The printed ID is the single source of
            identity — onboarding fills it in for you rather than having it retyped.
          </p>
          {canEdit && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="maint-btn-secondary" onClick={() => setShowImport(true)}>
                <Upload size={16} /> Import CSV
              </button>
              <button className="maint-btn-primary" onClick={() => setShowMint(true)}>
                <Plus size={16} /> Mint batch
              </button>
            </div>
          )}
        </div>

        <div className="proc-tabs" style={{ marginTop: 16 }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              className={tab === t.key ? "active" : ""}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.key === "pool" && summary ? ` (${summary.total})` : ""}
            </button>
          ))}
        </div>

        {message && <p className="maint-success">{message}</p>}
        {error && <p className="maint-form-error">{error}</p>}

        {tab === "pool" && (
          <PoolTab
            summary={summary}
            refreshKey={refreshKey}
            onChanged={refresh}
            onError={setError}
          />
        )}
        {tab === "orders" && (
          <OrdersTab refreshKey={refreshKey} onChanged={refresh} onError={setError} />
        )}
        {tab === "sims" && (
          <SimsTab refreshKey={refreshKey} onChanged={refresh} onError={setError} />
        )}
        {tab === "manufacturers" && (
          <ManufacturersTab refreshKey={refreshKey} onChanged={refresh} onError={setError} />
        )}
        {tab === "batches" && <BatchesTab refreshKey={refreshKey} />}

        {showMint && (
          <MintBatchForm
            onClose={() => setShowMint(false)}
            onDone={(n, batch) => {
              setShowMint(false);
              refresh(`Minted ${n} device ID${n === 1 ? "" : "s"} into ${batch}`);
            }}
          />
        )}

        {showImport && (
          <ImportCsvForm
            onClose={() => setShowImport(false)}
            onDone={(res) => {
              setShowImport(false);
              refresh(
                `Imported ${res.added} ID${res.added === 1 ? "" : "s"} into ${res.batch_name}` +
                  (res.linked_onboarded
                    ? ` — ${res.linked_onboarded} matched an existing device`
                    : "") +
                  (res.skipped_existing ? `, ${res.skipped_existing} already in the pool` : "")
              );
            }}
          />
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default ProcurementConsole;
